import { Box, Button } from "@mui/material";
import { MedicineResponseDto, MedicineItemDto } from "libs/cellera-sdk";
import { useCallback, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { MedicineWithOrderDetails } from "types/medicine";

import MedicineDetailsDialog from "./MedicineDetailsDialog";
import MedicineSearchPanel from "./MedicineSearchPanel";
import OrderSummaryTable from "./OrderSummaryTable";

interface MedicineCreatorProps {
  onConfirm: (selectedMedicines: MedicineWithOrderDetails[]) => Promise<void>;
  onClose: () => void;
  isAdding: boolean;
}

function MedicineCreator({
  onConfirm,
  onClose,
  isAdding,
}: MedicineCreatorProps) {
  const { colors } = useCelleraTheme();

  const [selectedMedicine, setSelectedMedicine] =
    useState<MedicineResponseDto | null>(null);
  const [medicineCart, setMedicineCart] = useState<MedicineWithOrderDetails[]>(
    []
  );
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);

  const handleSelectMedicine = useCallback((medicine: MedicineResponseDto) => {
    setSelectedMedicine(medicine);
    setOpenDetailsDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDetailsDialog(false);
    setSelectedMedicine(null);
  }, []);

  const handleAddToCart = useCallback(
    (sig: string, dispense: number, refill: number) => {
      if (!selectedMedicine) {
        return;
      }

      const orderDetails: MedicineItemDto = {
        medicineId: selectedMedicine.id,
        sig,
        dispense,
        refill,
      };

      setMedicineCart((prev) => [
        ...prev,
        { medicine: selectedMedicine, orderDetails },
      ]);
    },
    [selectedMedicine]
  );

  const handleRemoveFromCart = useCallback((index: number) => {
    setMedicineCart((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (medicineCart.length === 0) {
      return;
    }

    await onConfirm(medicineCart);
    setMedicineCart([]);
    onClose();
  }, [medicineCart, onConfirm, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const isConfirmDisabled = medicineCart.length === 0 || isAdding;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}
    >
      <Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 0 }}>
        {/* Left Panel - Medicine Search */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MedicineSearchPanel onSelectMedicine={handleSelectMedicine} />
        </Box>

        {/* Right Panel - Order Summary (Table) */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <OrderSummaryTable
            medicineCart={medicineCart}
            onRemoveFromCart={handleRemoveFromCart}
          />
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          pb: 2,
          borderBottom: `1px solid ${colors.border.main}`,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={isAdding}
          sx={{
            textTransform: "none",
            borderColor: colors.border.main,
            color: colors.text.primary,
            "&:hover": {
              borderColor: colors.border.dark,
              backgroundColor: colors.background.secondary,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          sx={{
            textTransform: "none",
            backgroundColor: colors.secondary.main,
            "&:hover": {
              backgroundColor: colors.secondary.dark,
            },
            "&.Mui-disabled": {
              backgroundColor: colors.border.main,
              color: colors.text.disabled,
            },
          }}
        >
          {isAdding
            ? "Adding..."
            : `Confirm (${medicineCart.length} ${
                medicineCart.length === 1 ? "item" : "items"
              })`}
        </Button>
      </Box>

      {/* Medicine Details Dialog */}
      <MedicineDetailsDialog
        open={openDetailsDialog}
        medicine={selectedMedicine}
        onClose={handleCloseDialog}
        onAddToCart={handleAddToCart}
      />
    </Box>
  );
}

export default MedicineCreator;
